import { useMemo } from 'react';
import {
  useListCrmEscalationsQuery, useListCrmEventsQuery, type CrmEscalation, type CrmEvent,
} from '../../store/api/crmPhylaxApi';
import { usePuedeVerPhylaxCrm } from './usePuedeVerPhylaxCrm';

/**
 * LO QUE EL AGENTE VIRTUAL DEJÓ PENDIENTE, contado una sola vez. (03/11/2026)
 *
 * Consultas sin responder y visitas por coordinar (por confirmar o ya pasadas sin cerrar).
 * Lo usan tres lugares que antes contaban cada uno a su manera: el widget "Te están
 * esperando" del inicio, el número del menú "Phylax" (que contaba SOLO consultas) y las
 * pestañas Consultas y Eventos de la sección, que no mostraban nada. Desde Harmony lo
 * pidieron así: "que cuando se generan visitas en Phylax aparezca ahí, en eventos, un
 * numerito… porque si no se pierden".
 */

/**
 * Cuánto se espera después de la hora de la visita antes de darla por VENCIDA. Un día,
 * el mismo margen que "Visitas sin cerrar" (`visitasSinCerrar.service.ts`): la de las
 * 15:00 no está vencida a las 17:00 —todavía se confirma si el cliente avisó que llega
 * tarde— pero la de anteayer sí.
 */
export const GRACIA_HORAS = 24;
/**
 * Hasta dónde se mira para atrás. Más allá de dos meses cerrar una visita no es una
 * acción, es arqueología, y nadie se acuerda de qué pasó.
 */
export const VENCIDAS_DIAS = 60;

export const horasDesde = (d: Date) => Math.max(0, (Date.now() - d.getTime()) / 3_600_000);

/**
 * Cuándo cae la visita, en hora de Montevideo.
 *
 * Phylax manda `date` (YYYY-MM-DD) y `time` (HH:MM) por separado, y `new Date('2026-08-14')`
 * se parsea como medianoche UTC: sin armar la fecha con el offset, una visita de hoy a las
 * 21:00 se leía como del día anterior. Sin hora se toma el final del día, para no dar por
 * vencida una visita que todavía puede pasar.
 */
export function cuandoEs(v: CrmEvent): Date {
  const hora = (v.time ?? '').slice(0, 5) || '23:59';
  const d = new Date(`${v.date ?? ''}T${hora}:00-03:00`);
  return Number.isNaN(d.getTime()) ? new Date(v.date ?? Date.now()) : d;
}

/** Ya pasó su hora (con la gracia) y nadie la cerró. */
export const estaVencida = (v: CrmEvent) => horasDesde(cuandoEs(v)) >= GRACIA_HORAS;

/**
 * Varias consultas del MISMO cliente por la MISMA propiedad son una sola charla partida
 * en pedazos: el Agente escala cada vuelta. Se cuentan como una, la más vieja primero.
 */
export function agruparConsultas(escalations: CrmEscalation[]): Array<{ e: CrmEscalation; extras: number }> {
  const porCharla = new Map<string, CrmEscalation[]>();
  for (const e of escalations) {
    const k = `${e.customerName || e.phone}|${e.propertyId ?? ''}`;
    porCharla.set(k, [...(porCharla.get(k) ?? []), e]);
  }
  return [...porCharla.values()]
    .map((g) => {
      const orden = [...g].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
      return { e: orden[0], extras: orden.length - 1 };
    })
    .sort((a, b) => +new Date(a.e.createdAt) - +new Date(b.e.createdAt));
}

/**
 * Las visitas en dos trabajos: las que vienen (hay que confirmarlas) y las que ya pasaron
 * sin cerrar (hay que decir si se hicieron). Una CONFIRMADA entra solo si ya pasó. Las de
 * más de `VENCIDAS_DIAS` no entran. Ver el porqué en PhylaxPendientesWidget.
 */
export function partirVisitas(pendientes: CrmEvent[], confirmadas: CrmEvent[]): { porVenir: CrmEvent[]; vencidas: CrmEvent[] } {
  const porVenir: CrmEvent[] = [];
  const vencidas: CrmEvent[] = [];
  for (const v of pendientes) {
    if (!estaVencida(v)) porVenir.push(v);
    else if (horasDesde(cuandoEs(v)) <= VENCIDAS_DIAS * 24) vencidas.push(v);
  }
  for (const v of confirmadas) {
    if (estaVencida(v) && horasDesde(cuandoEs(v)) <= VENCIDAS_DIAS * 24) vencidas.push(v);
  }
  const porFecha = (a: CrmEvent, b: CrmEvent) => +cuandoEs(a) - +cuandoEs(b);
  return { porVenir: porVenir.sort(porFecha), vencidas: vencidas.sort(porFecha) };
}

/**
 * Los números para el menú y las pestañas. Mismas consultas que el widget (RTK Query las
 * comparte: no se pide dos veces). El backend ya recorta a lo que este usuario ve.
 */
export function usePendientesDelAgente(): { consultas: number; visitas: number; total: number } {
  const puedeVer = usePuedeVerPhylaxCrm();
  const opts = { skip: !puedeVer, pollingInterval: 120_000 };
  const { data: esc } = useListCrmEscalationsQuery({ status: 'pending' }, opts);
  const { data: pend } = useListCrmEventsQuery({ status: 'pending' }, opts);
  const { data: conf } = useListCrmEventsQuery({ status: 'confirmed' }, opts);
  return useMemo(() => {
    const consultas = agruparConsultas(esc?.escalations ?? []).length;
    const { porVenir, vencidas } = partirVisitas(pend?.events ?? [], conf?.events ?? []);
    const visitas = porVenir.length + vencidas.length;
    return { consultas, visitas, total: consultas + visitas };
  }, [esc, pend, conf]);
}
